import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuVolume1, LuVolume2, LuVolumeX } from 'react-icons/lu';
import useMediaQuery from '../../hooks/useMediaQuery';

const VolumeIcon = ({ muted, volume }) => {
  const Icon = muted ? LuVolumeX : volume < 0.5 ? LuVolume1 : LuVolume2;
  return <Icon className="tw-h-[18px] tw-w-[18px]" />;
};

VolumeIcon.propTypes = { muted: PropTypes.bool.isRequired, volume: PropTypes.number.isRequired };

/** Thin track with a fill and a thumb over a transparent native range (keyboard and touch work). */
const VolumeSlider = ({ value, label, dark, onChange, className }) => {
  const percent = Math.round(value * 100);
  return (
    <div className={`tw-relative tw-h-8 ${className}`}>
      <span className={`tw-absolute tw-inset-x-0 tw-top-1/2 tw-h-1.5 -tw-translate-y-1/2 tw-overflow-hidden tw-rounded-full ${dark ? 'tw-bg-white/20' : 'tw-bg-slate-200 dark:tw-bg-white/10'}`}>
        <span className="tw-block tw-h-full tw-rounded-full tw-bg-gradient-to-r tw-from-teal-400 tw-to-cyan-500" style={{ width: `${percent}%` }} />
      </span>
      <span style={{ left: `${percent}%` }} className="tw-pointer-events-none tw-absolute tw-top-1/2 tw-h-3.5 tw-w-3.5 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-rounded-full tw-border-2 tw-border-white tw-bg-teal-500 tw-shadow" />
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={percent}
        onChange={(event) => onChange(Number(event.target.value) / 100)}
        aria-label={label}
        aria-valuetext={`%${percent}`}
        title={`${label}: %${percent}`}
        className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-cursor-pointer tw-appearance-none tw-bg-transparent tw-opacity-0"
      />
    </div>
  );
};

VolumeSlider.propTypes = {
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  dark: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
};

/**
 * Narration volume for the tutorial videos: a mute button with a level slider.
 * Wide screens show the slider inline; on phones the button opens a small
 * panel holding the mute switch and the slider, so the control bar still fits.
 * `labels`: { mute, unmute, volume, unavailable }.
 */
const VolumeControl = ({ volume, muted, available, dark, labels, onToggleMute, onVolume }) => {
  const wide = useMediaQuery('(min-width: 640px)');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const silent = muted || volume === 0;
  const buttonLabel = !available ? labels.unavailable : silent ? labels.unmute : labels.mute;
  const buttonClass = `tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-transparent tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 disabled:tw-cursor-not-allowed disabled:tw-opacity-40 ${
    dark
      ? 'tw-text-white/80 hover:tw-bg-white/10 hover:tw-text-white'
      : 'tw-text-slate-600 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-900 dark:tw-text-slate-300 dark:hover:tw-bg-white/10 dark:hover:tw-text-white'
  }`;

  // The phone panel closes on a tap elsewhere or when the screen grows wide
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  useEffect(() => {
    if (wide) setOpen(false);
  }, [wide]);

  if (wide || !available) {
    return (
      <div className="tw-flex tw-shrink-0 tw-items-center">
        <button
          type="button"
          onClick={onToggleMute}
          disabled={!available}
          title={buttonLabel}
          aria-label={buttonLabel}
          aria-pressed={available ? !silent : undefined}
          className={buttonClass}
        >
          <VolumeIcon muted={silent || !available} volume={volume} />
        </button>
        {available && <VolumeSlider value={silent ? 0 : volume} label={labels.volume} dark={dark} onChange={onVolume} className="tw-mr-2 tw-w-20 lg:tw-w-24" />}
      </div>
    );
  }

  return (
    <div ref={rootRef} className="tw-relative tw-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        title={labels.volume}
        aria-label={labels.volume}
        aria-expanded={open}
        className={buttonClass}
      >
        <VolumeIcon muted={silent} volume={volume} />
      </button>
      {open && (
        <div
          className={`tw-absolute tw-bottom-full tw-right-0 tw-z-20 tw-mb-2 tw-flex tw-w-56 tw-items-center tw-gap-1 tw-rounded-2xl tw-border tw-px-1.5 tw-py-1 tw-shadow-xl ${
            dark ? 'tw-border-white/10 tw-bg-slate-900/95' : 'tw-border-slate-200 tw-bg-white dark:tw-border-white/10 dark:tw-bg-[#1f232b]'
          }`}
        >
          <button
            type="button"
            onClick={onToggleMute}
            title={silent ? labels.unmute : labels.mute}
            aria-label={silent ? labels.unmute : labels.mute}
            aria-pressed={!silent}
            className={buttonClass}
          >
            <VolumeIcon muted={silent} volume={volume} />
          </button>
          <VolumeSlider value={silent ? 0 : volume} label={labels.volume} dark={dark} onChange={onVolume} className="tw-flex-1" />
          <span className={`tw-w-10 tw-shrink-0 tw-text-right tw-text-xs tw-font-semibold tw-tabular-nums ${dark ? 'tw-text-white/80' : 'tw-text-slate-600 dark:tw-text-slate-300'}`}>
            %{Math.round((silent ? 0 : volume) * 100)}
          </span>
        </div>
      )}
    </div>
  );
};

VolumeControl.propTypes = {
  volume: PropTypes.number.isRequired,
  muted: PropTypes.bool.isRequired,
  available: PropTypes.bool.isRequired,
  dark: PropTypes.bool,
  labels: PropTypes.shape({
    mute: PropTypes.string.isRequired,
    unmute: PropTypes.string.isRequired,
    volume: PropTypes.string.isRequired,
    unavailable: PropTypes.string.isRequired,
  }).isRequired,
  onToggleMute: PropTypes.func.isRequired,
  onVolume: PropTypes.func.isRequired,
};

export default VolumeControl;
